import React from 'react';
import { Box } from '@mui/material';
import EmptyState from './EmptyState';
import Header from './Header';
import MessageFeed from './MessageFeed';
import Composer from './Composer';
import useChatScroll from '../../hooks/useChatScroll';
import useChatComposer from '../../hooks/useChatComposer';
import useInterveneTheme from '../../hooks/useInterveneTheme';
import {
  canEndInterveneSession,
  canShowSessionToggle,
  canStartInterveneSession,
  isInterveneSessionActive,
} from '../../utils/sessionToggleRules';
import { userOwnsChat } from '../../utils/chatOwnership';
import { getComposerMode } from '../../utils/composerMode';
import useIntervenePermissions from '../../hooks/useIntervenePermissions';
import { InterveneAutomationProvider } from '../../context/InterveneAutomationContext';
import { useOptoutCheck } from '../../../whatsapp-templates/services/optoutService';

export default function ChatPanel({
  activeUser,
  messages,
  isLoadingMessages,
  isLoadingOlder,
  hasMoreMessages,
  draftMessage,
  onDraftChange,
  isSending,
  onSendMessage,
  onToggleSession,
  onLoadOlderMessages,
  shouldStickToBottomRef,
  userType,
  onCall,
  onSendTemplate,
  onToggleProfile,
  isProfileOpen,
  onBackToList,
}) {
  const { chat } = useInterveneTheme();
  const { canUseChat, canUseCalling, currentUserId } = useIntervenePermissions();
  const { isOptedOut } = useOptoutCheck();
  const panelSx = {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    height: '100%',
    width: '100%',
    backgroundColor: chat.message?.feed?.background || chat.background,
  };

  const { scrollRef, handleScroll } = useChatScroll({
    messages,
    isLoadingMessages,
    isLoadingOlder,
    hasMoreMessages,
    onLoadOlderMessages,
    shouldStickToBottomRef,
    activeUserPhone: activeUser?.phone_number,
  });

  const composer = useChatComposer({ onSendMessage, shouldStickToBottomRef });

  if (!activeUser?.phone_number) {
    return (
      <Box sx={panelSx}>
        <EmptyState />
      </Box>
    );
  }

  const isInterveneActive = isInterveneSessionActive(activeUser);
  const isHandledByOther =
    isInterveneActive && !userOwnsChat(activeUser, currentUserId);
  const handledByName = activeUser?.intervened_by || activeUser?.last_intervened_by || '';

  const isUserOptedOut = isOptedOut(activeUser?.phone_number);
  const showSessionToggle =
    !isHandledByOther && canUseChat && canShowSessionToggle(userType) && !isUserOptedOut;
  const canStartSession =
    !isHandledByOther &&
    canUseChat &&
    canStartInterveneSession(userType, activeUser) &&
    !isUserOptedOut;
  const canEndSession = !isHandledByOther && canUseChat && canEndInterveneSession(userType, activeUser);
  const composerMode = getComposerMode(userType, activeUser);
  const conversationKey = `${activeUser.phone_number}:${activeUser.bot_number || ''}`;

  return (
    <InterveneAutomationProvider composerMode={composerMode}>
      <Box sx={panelSx}>
        <Box sx={{ flexShrink: 0, minHeight: 0 }}>
          <Header
            activeUser={activeUser}
            messages={messages}
            composerMode={composerMode}
            showSessionToggle={showSessionToggle}
            canStartSession={canStartSession}
            canEndSession={canEndSession}
            onStartSession={onToggleSession}
            onEndSession={onToggleSession}
            onToggleProfile={onToggleProfile}
            isProfileOpen={isProfileOpen}
            onCall={canUseCalling ? onCall : undefined}
            onBackToList={onBackToList}
            isHandledByOther={isHandledByOther}
            handledByName={handledByName}
          />
        </Box>

        <MessageFeed
          key={conversationKey}
          scrollRef={scrollRef}
          onScroll={handleScroll}
          messages={messages}
          activeUser={activeUser}
          isLoadingMessages={isLoadingMessages}
          isLoadingOlder={isLoadingOlder}
          hasMoreMessages={hasMoreMessages}
        />

        {!isHandledByOther && (
          <Composer
            draftMessage={draftMessage}
            onDraftChange={onDraftChange}
            isSending={isSending}
            composerMode={composerMode}
            canUseChat={canUseChat}
            isOptedOut={isUserOptedOut}
            onSendTemplate={canUseChat ? onSendTemplate : undefined}
            onStartSession={canStartSession ? onToggleSession : undefined}
            composer={composer}
          />
        )}
      </Box>
    </InterveneAutomationProvider>
  );
}
